import type { TuningIdentity, TuningLocalEvent, TuningLocalEventPage } from '@playcastdotio/PeerCore';

type Ring = { next: number; bytes: number; entries: { event: TuningLocalEvent; bytes: number; order: number }[] };
/** Stable session cursors and both byte/count bounds, independent of trial IDs. */
export class HostTuningEventManager {
  private readonly rings = new Map<string, Ring>();
  private bytes = 0; private count = 0; private order = 0;
  private readonly listeners = new Set<(event: TuningLocalEvent) => void>();
  constructor(private readonly limits = { sessionCount: 2_000, sessionBytes: 2_097_152,
    globalCount: 10_000, globalBytes: 8_388_608 }) {}
  public append(target: TuningIdentity, endpoint: TuningLocalEvent['endpoint'],
    kind: TuningLocalEvent['kind'], payload: TuningLocalEvent['payload']): TuningLocalEvent {
    const ring = this.rings.get(target.sessionId) ?? { next: 1, bytes: 0, entries: [] };
    this.rings.set(target.sessionId, ring);
    const event: TuningLocalEvent = { sequence: ring.next++, target: { ...target }, endpoint, kind, payload: structuredClone(payload) };
    const bytes = new TextEncoder().encode(JSON.stringify(event)).byteLength;
    ring.entries.push({ event, bytes, order: this.order++ }); ring.bytes += bytes; this.bytes += bytes; this.count++;
    while (ring.entries.length > this.limits.sessionCount || ring.bytes > this.limits.sessionBytes) this.evict(ring);
    while (this.count > this.limits.globalCount || this.bytes > this.limits.globalBytes) {
      const oldest = [...this.rings.values()].filter(candidate => candidate.entries.length)
        .sort((a, b) => a.entries[0].order - b.entries[0].order)[0];
      if (!oldest) break; this.evict(oldest);
    }
    for (const listener of this.listeners) listener(structuredClone(event));
    return event;
  }
  public page(sessionId: string, after: number, limit: number, trialId?: string): TuningLocalEventPage {
    const ring = this.rings.get(sessionId);
    if (!ring) return { events: [], nextCursor: after, oldestAvailableCursor: 0, gap: false };
    const oldest = ring.entries[0]?.event.sequence ?? ring.next;
    const events: TuningLocalEvent[] = []; let nextCursor = after;
    for (const { event } of ring.entries) {
      if (event.sequence <= after) continue;
      nextCursor = event.sequence;
      if (!trialId || event.target.trialId === trialId) events.push(structuredClone(event));
      if (events.length >= Math.max(1, Math.min(200, limit))) break;
    }
    return { events, nextCursor, oldestAvailableCursor: oldest, gap: after < oldest - 1 };
  }
  public subscribe(listener: (event: TuningLocalEvent) => void): () => void {
    this.listeners.add(listener); return () => this.listeners.delete(listener);
  }
  public dispose(): void { this.rings.clear(); this.listeners.clear(); this.bytes = 0; this.count = 0; }
  private evict(ring: Ring): void {
    const entry = ring.entries.shift(); if (!entry) return;
    ring.bytes -= entry.bytes; this.bytes -= entry.bytes; this.count--;
  }
}
